// ─── AppletEmbedPage — /project/applet/{text,html}/:projectId ────────────────
//
// Public, unauthenticated page served from the React SPA. It is the
// live target of the "Embedded Data Applet" iframe snippets generated
// in Settings → Embedded Data Applet (EmbeddedDataAppletPanel.jsx),
// e.g. <iframe src="…/project/applet/text/2503?refresh=true">. Third-
// party sites embed it with no session / JWT, so there is NO
// ProtectedRoute wrapper (see AppRoutes.jsx).
//
// It renders the same stylized card shown in that settings modal's
// right-side preview panel (AppletPreview) — project name header,
// "At <time>" reading blocks, name/value/unit rows, and a
// "Powered By WQData LIVE" footer — but with LIVE data pulled from the
// public endpoint:
//
//   GET /api/project/applet/<projectId>/data/
//     → { project_name, timezone, allow_embed, all_data_link,
//         groups: [{ site_name, device_name,
//                    times: [{ time, items: [{name,value,unit}] }] }],
//         latest_time }
//
// The `allow_embed` gate is honored server-side (data is withheld when
// the owner hasn't opted in); this page renders the matching notice.
//
// `?refresh=true` (the flag the iframe snippet sets) turns on a 60s
// auto-refresh poll — matching the legacy Symfony applet behavior.
import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import axios from 'axios'

const API_URL       = import.meta.env.VITE_API_URL       || ''
const API_BASE_PATH = import.meta.env.VITE_API_BASE_PATH || '/api'

const REFRESH_MS = 60_000

export default function AppletEmbedPage({ variant = 'html' }) {
  const { projectId } = useParams()
  const [searchParams] = useSearchParams()
  // Any truthy-ish value except the literal string "false" enables
  // auto-refresh — mirrors the legacy `refresh && refresh != 'false'`.
  const refresh = (() => {
    const v = searchParams.get('refresh')
    return v != null && v !== 'false' && v !== '0'
  })()

  const [data,    setData]    = useState(null)
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState(null)
  const timerRef = useRef(null)

  useEffect(() => {
    if (!projectId) {
      setError('Missing project id')
      setLoading(false)
      return
    }
    let cancelled = false

    const load = (isInitial) => {
      if (isInitial) { setLoading(true); setError(null) }
      axios.get(`${API_URL}${API_BASE_PATH}/project/applet/${projectId}/data/`)
        .then((res) => { if (!cancelled) { setData(res.data || null); setError(null) } })
        .catch((e) => {
          if (cancelled) return
          // 404 → project not found; other errors → generic message.
          if (e?.response?.status === 404) setError('not_found')
          else if (isInitial) setError(e?.message || 'Failed to load applet')
          // On a background poll failure, keep the last good data.
        })
        .finally(() => { if (!cancelled && isInitial) setLoading(false) })
    }

    load(true)
    if (refresh) {
      timerRef.current = setInterval(() => load(false), REFRESH_MS)
    }
    return () => {
      cancelled = true
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [projectId, refresh])

  const projectName = data?.project_name || `Project #${projectId}`

  // Flatten the device-grouped payload into the "At <time>" blocks the
  // preview card renders. Each device contributes its time buckets; we
  // prefix the block label with the site/device when there is more than
  // one device so a multi-site applet stays legible.
  const blocks = useMemo(() => {
    const groups = data?.groups || []
    const multiDevice = groups.length > 1
    const out = []
    for (const g of groups) {
      for (const t of (g.times || [])) {
        out.push({
          label:   t.time ? `At ${t.time}` : 'No data found',
          context: multiDevice
            ? [g.site_name, g.device_name].filter(Boolean).join(' - ')
            : '',
          items: t.items || [],
        })
      }
    }
    return out
  }, [data])

  const plain = variant === 'text'

  return (
    <div className={plain ? 'applet-embed applet-embed--plain' : 'applet-embed'}>
      <style>{CARD_CSS}</style>

      {loading && (
        <div className="applet-card">
          <div className="applet-body applet-muted">Loading…</div>
        </div>
      )}

      {!loading && error === 'not_found' && (
        <div className="applet-card">
          <div className="applet-body applet-muted">This applet is not available.</div>
        </div>
      )}

      {!loading && error && error !== 'not_found' && (
        <div className="applet-card">
          <div className="applet-body applet-error">{error}</div>
        </div>
      )}

      {/* allow_embed gate — matches the legacy text.html.twig notice. */}
      {!loading && !error && data && !data.allow_embed && (
        <div className="applet-card">
          <div className="applet-head">{projectName}</div>
          <div className="applet-body applet-muted">
            Viewing real-time data in this applet has been disabled.
          </div>
          <AppletFooter />
        </div>
      )}

      {!loading && !error && data && data.allow_embed && (
        <div className="applet-card">
          <div className="applet-head" title={projectName}>{projectName}</div>
          <div className="applet-body">
            {blocks.length === 0 ? (
              <div className="applet-muted">No parameters selected for this applet.</div>
            ) : (
              blocks.map((b, i) => (
                <div key={i} className="applet-block">
                  {b.context && <div className="applet-context">{b.context}</div>}
                  <div className="applet-time">{b.label}</div>
                  <div className="applet-rows">
                    {b.items.map((it, j) => (
                      <div key={j} className="applet-row">
                        <span className="applet-name" title={it.name}>{it.name}</span>
                        <span className="applet-value">
                          {it.value}{it.unit ? ` ${it.unit}` : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
          <AppletFooter />
        </div>
      )}
    </div>
  )
}

function AppletFooter() {
  return (
    <div className="applet-foot">
      Powered By{' '}
      <a href="https://www.wqdatalive.com" target="_blank" rel="noreferrer">WQData LIVE</a>
    </div>
  )
}

// Self-contained styles — the page is rendered inside a third-party
// iframe, so it can't rely on the app's global Tailwind build being
// present. Colors mirror the in-app AppletPreview card.
const CARD_CSS = `
  .applet-embed { font-family: system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
    color: #1f2937; padding: 4px; box-sizing: border-box; }
  .applet-embed * { box-sizing: border-box; }
  .applet-card { border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden;
    background: #ffffff; max-width: 100%; }
  .applet-embed--plain .applet-card { border: none; border-radius: 0; background: transparent; }
  .applet-head { padding: 10px 14px; border-bottom: 1px solid #f3f4f6;
    font-size: 14px; font-weight: 600; color: #1f2937; white-space: nowrap;
    overflow: hidden; text-overflow: ellipsis; }
  .applet-body { padding: 10px 14px; font-size: 13px; }
  .applet-block + .applet-block { margin-top: 14px; }
  .applet-context { font-size: 11px; font-weight: 600; color: #6b7280; margin-bottom: 2px; }
  .applet-time { font-size: 11px; color: #6b7280; text-align: center; margin-bottom: 4px; }
  .applet-row { display: flex; align-items: center; justify-content: space-between;
    border-bottom: 1px solid #f3f4f6; padding-bottom: 4px; margin-bottom: 4px; }
  .applet-name { color: #374151; margin-right: 8px; overflow: hidden;
    text-overflow: ellipsis; white-space: nowrap; }
  .applet-value { color: #1f2937; font-weight: 500; white-space: nowrap; }
  .applet-muted { color: #6b7280; font-style: italic; }
  .applet-error { color: #b91c1c; }
  .applet-foot { padding: 8px 14px; text-align: center; font-size: 11px;
    color: #6b7280; background: #f9fafb; }
  .applet-foot a { color: #358ECD; text-decoration: none; }
  .applet-foot a:hover { text-decoration: underline; }
`
